import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteBranding as deleteBrandingAPI,
  fetchBranding,
  saveBranding,
} from '../api';
import { BrandingImages } from '../types';

export const useBranding = () => {
  const queryClient = useQueryClient();

  const {
    data: branding,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['branding'],
    queryFn: fetchBranding,
  });

  const { mutateAsync: saveBrandingMutation, isPending: isSaving } =
    useMutation({
      mutationFn: saveBranding,
      onMutate: async (formData: FormData) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['branding'] });

        // Snapshot the previous value
        const previousBranding = queryClient.getQueryData<BrandingImages>([
          'branding',
        ]);

        // Get the image type and file from FormData
        const imageType = formData.get('image_type') as string;
        const imageFile = formData.get('image') as File;

        if (imageFile && imageType) {
          // Create object URL for immediate preview
          const optimisticImageUrl = URL.createObjectURL(imageFile);

          // Optimistically update the cache
          queryClient.setQueryData<BrandingImages>(['branding'], (old) => ({
            ...old,
            [imageType]: optimisticImageUrl,
          }));
        }

        // Return context object with the snapshot value
        return { previousBranding };
      },
      onSuccess: (data) => {
        queryClient.setQueryData(
          ['branding'],
          (oldData: BrandingImages | undefined) => {
            if (!oldData) return data;

            let hasChanges = false;
            const updatedData: BrandingImages = { ...oldData };

            if (data.logo && data.logo !== '') {
              updatedData.logo = data.logo;
              hasChanges = true;
            }
            if (data.letterhead && data.letterhead !== '') {
              updatedData.letterhead = data.letterhead;
              hasChanges = true;
            }
            if (data.follower && data.follower !== '') {
              updatedData.follower = data.follower;
              hasChanges = true;
            }

            return hasChanges ? updatedData : oldData;
          },
        );
      },
      onError: (_err, _variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousBranding) {
          queryClient.setQueryData(['branding'], context.previousBranding);
        }
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have correct data
        void queryClient.invalidateQueries({ queryKey: ['branding'] });
      },
    });

  const { mutateAsync: deleteBrandingMutation, isPending: isDeleting } =
    useMutation({
      mutationFn: ({
        imageId,
        imageType,
      }: {
        imageId: string;
        imageType: string;
      }) => deleteBrandingAPI(imageId, imageType),
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey: ['branding'] });

        // Snapshot the previous value
        const previousBranding = queryClient.getQueryData<BrandingImages>([
          'branding',
        ]);

        // Optimistically update the cache
        queryClient.setQueryData<BrandingImages>(['branding'], (old) => {
          if (!old) return old;
          return {
            ...old,
            [variables.imageType]: '',
          };
        });

        // Return context object with the snapshot value
        return { previousBranding };
      },
      onSuccess: (_, variables) => {
        queryClient.setQueryData(
          ['branding'],
          (oldData: BrandingImages | undefined) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              [variables.imageType]: '',
            };
          },
        );
      },
      onError: (_err, _variables, context) => {
        // If the mutation fails, use the context returned from onMutate to roll back
        if (context?.previousBranding) {
          queryClient.setQueryData(['branding'], context.previousBranding);
        }
      },
      onSettled: () => {
        // Always refetch after error or success to ensure we have correct data
        void queryClient.invalidateQueries({ queryKey: ['branding'] });
      },
    });

  return {
    branding,
    isLoading,
    isError,
    error,
    saveBranding: saveBrandingMutation,
    isSaving,
    deleteBranding: deleteBrandingMutation,
    isDeleting,
    refetch,
  };
};
